import { useEffect, useMemo, useRef, useState } from 'react';
import AssessmentFlow from '../AssessmentFlow.jsx';
import { setToken } from '../api.js';
import { embedSettings } from '../config/runtime.js';
import { embedDrafts } from '../engine/embed.js';
import { createHeightReporter, createMessenger, listenForHandoff } from './protocol.js';
import '../styles/embed.css';

const EMBED_FONT_HREF = 'https://fonts.googleapis.com/css2?family=Nunito+Sans:wght@400;600;700;800&display=swap';

// /embed — the assessment inside PompaBooking's prep page (NTAI-EMB-03).
//
// Inert unless the deploy-time config.json has `embedEnabled: true`: with the flag off
// this renders nothing, listens to nothing and sends nothing. When on, it waits for a
// `pb:handoff` from an allowed parent origin, trades it for an NTAi session
// (POST /api/pb/exchange), stores that session the way the app always does, then runs
// the normal flow with the booking-only pieces hidden.
export default function EmbedApp({
  settings = embedSettings(),
  selfWindow = typeof window !== 'undefined' ? window : null,
  parentWindow = typeof window !== 'undefined' ? window.parent : null,
  exchange,
}) {
  const [session, setSession] = useState(null); // { account, prefill }
  const messengerRef = useRef(null);
  const framed = !!(selfWindow && parentWindow && parentWindow !== selfWindow);
  const active = settings.enabled && settings.parents.length > 0 && framed;

  useEffect(() => {
    if (!active) return undefined;
    // Embed theme: a class on the document root + PB's client-page font. No analytics.
    const root = document.documentElement;
    root.classList.add('ntai-embed-doc');
    let link = null;
    if (!document.querySelector(`link[href="${EMBED_FONT_HREF}"]`)) {
      link = document.createElement('link');
      link.rel = 'stylesheet';
      link.href = EMBED_FONT_HREF;
      document.head.appendChild(link);
    }
    const messenger = createMessenger(parentWindow, settings.parents);
    messengerRef.current = messenger;
    const stop = listenForHandoff({
      selfWindow,
      parentWindow,
      parents: settings.parents,
      messenger,
      exchange,
      onSession: (data) => {
        setToken(data.token);
        setSession({ account: data.account, prefill: data.prefill || {} });
      },
    });
    return () => {
      stop();
      root.classList.remove('ntai-embed-doc');
      if (link) link.remove();
    };
  }, [active]); // eslint-disable-line react-hooks/exhaustive-deps

  if (!active) return null;
  return session
    ? <EmbeddedFlow session={session} messenger={messengerRef.current} />
    : <div className="ntai-embed"><p className="embed-wait">Loading your questions…</p></div>;
}

function EmbeddedFlow({ session, messenger }) {
  const rootRef = useRef(null);
  const lastPct = useRef(null);
  const doneSent = useRef(false);

  // Height: report the content's height to the parent (throttled) so the iframe fits it.
  useEffect(() => {
    const el = rootRef.current;
    if (!el || typeof ResizeObserver === 'undefined') return undefined;
    const reporter = createHeightReporter((px) => messenger.height(px));
    const ro = new ResizeObserver(() => reporter.report(el.scrollHeight));
    ro.observe(el);
    reporter.report(el.scrollHeight);
    return () => { ro.disconnect(); reporter.stop(); };
  }, [messenger]);

  const embed = useMemo(() => ({
    drafts: embedDrafts(session.prefill, session.account),
    accountEmail: (session.account.email || '').toLowerCase(),
    onProgress: (pct) => {
      if (pct === lastPct.current) return;
      lastPct.current = pct;
      messenger.progress(pct);
    },
    onDone: () => {
      if (doneSent.current) return;
      doneSent.current = true;
      messenger.done();
    },
  }), [session, messenger]);

  return (
    <div className="ntai-embed" ref={rootRef}>
      <AssessmentFlow enrolled={!!session.account.enrolled} embed={embed} />
    </div>
  );
}
